import { addItems } from './fablair-commerce.js';

if (!customElements.get('rs-editorial-video')) {
  customElements.define('rs-editorial-video', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-rs-video-toggle]');
      if (!this.video || !this.button) return;
      this.button.hidden = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.userStarted = false;
      this.button.addEventListener('click', () => {
        if (this.video.paused) { this.userPaused = false; this.userStarted = true; this.play(); }
        else { this.userPaused = true; this.video.pause(); }
      }, { signal });
      this.video.addEventListener('play', () => this.updateButton(), { signal });
      this.video.addEventListener('pause', () => this.updateButton(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      }, { threshold: 0.12 });
      this.observer.observe(this);
      this.reducedMotion.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.updateButton();
    }
    sync() {
      const wantsPlayback = this.userStarted || (this.dataset.autoplay === 'true' && !this.reducedMotion.matches);
      if (this.visible && !document.hidden && wantsPlayback && !this.userPaused) this.play();
      else this.video.pause();
    }
    play() { this.video.play().catch(() => this.updateButton()); }
    updateButton() {
      const paused = this.video.paused;
      this.button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      const icon = this.button.querySelector('[data-rs-video-icon]');
      if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.video?.pause(); }
  });
}
if (!customElements.get('rs-featured-product')) {
  customElements.define('rs-featured-product', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-rs-variant]')) return;
        const variant = event.target.selectedOptions[0];
        if (!variant) return;
        this.querySelector('[data-rs-price]').textContent = variant.dataset.price || '';
        const compare = this.querySelector('[data-rs-compare]');
        compare.textContent = variant.dataset.compare || '';
        compare.hidden = !variant.dataset.compare;
        this.querySelector('[data-rs-quantity]').value = variant.dataset.quantity || '1';
        this.querySelector('[data-rs-add]').disabled = variant.disabled || !variant.value;
        this.setImage(variant.dataset.image, variant.dataset.alt);
      }, { signal });
      this.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-rs-photo]');
        if (thumbnail) this.setImage(thumbnail.dataset.rsPhoto, thumbnail.dataset.rsPhotoAlt);
      }, { signal });
      this.querySelector('form')?.addEventListener('submit', (event) => this.submit(event), { signal });
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      const select = this.querySelector('[data-rs-variant]');
      const variant = select?.selectedOptions[0];
      const status = this.querySelector('[data-rs-cart-status]');
      if (!select?.reportValidity() || !variant?.value || variant.disabled) {
        if (status) status.textContent = 'Please choose an available option.';
        return;
      }
      const button = this.querySelector('[data-rs-add]');
      const quantity = Number(variant.dataset.quantity) || 1;
      this.busy = true;
      button.disabled = true;
      select.disabled = true;
      this.setAttribute('aria-busy', 'true');
      status.textContent = 'Adding to your bag…';
      try {
        await addItems(this, [{ id: variant.value, quantity }]);
        status.textContent = 'Added to your bag.';
      } catch (error) {
        status.textContent = error.message;
      } finally {
        this.busy = false;
        button.disabled = Boolean(select.selectedOptions[0]?.disabled);
        select.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
    setImage(src, alt) {
      const image = this.querySelector('[data-rs-product-image]');
      if (!image) return;
      image.hidden = !src;
      if (!src) return;
      image.src = src;
      image.removeAttribute('srcset');
      image.alt = alt || '';
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}
if (!customElements.get('rs-testimonials')) {
  customElements.define('rs-testimonials', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.addEventListener('click', (event) => { const tab = event.target.closest('[role="tab"]'); if (this.tabs.includes(tab)) this.activate(tab); }, { signal });
      this.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? direction : -direction) + this.tabs.length) % this.tabs.length;
        this.activate(this.tabs[next]); this.tabs[next].focus();
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const tab = this.tabs.find(item => item.dataset.blockId === event.detail?.blockId);
        if (tab) this.activate(tab);
      }, { signal });
    }
    activate(tab) {
      this.tabs.forEach(item => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected)); item.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(item.getAttribute('aria-controls'))}`);
        if (panel) panel.hidden = !selected;
      });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}
